import { useEffect, useState } from 'react';
import { Button } from '@marine/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@marine/ui/dialog';
import { Label } from '@marine/ui/label';
import { RadioGroup, RadioGroupItem } from '@marine/ui/radio-group';
import type { ConflictChoice, EditableBoat, FieldConflict } from '@/modules/inventory/utils/boat-changes';
import { describeChangePath, formatChangeValue } from '@/modules/inventory/utils/boat-change-labels';

interface ChangedElsewhereDialogProps {
  open: boolean;
  /** Must keep its identity while open: a new array resets the dealer's choices to "keep yours". */
  conflicts: FieldConflict[];
  /** The rebased state the conflicts are in — names each conflicting row. */
  merged?: EditableBoat;
  onResolve: (choices: Record<string, ConflictChoice>) => void;
  onCancel: () => void;
  /** The dismiss button's label (the canvas says "Keep mine": closing keeps your values, D45). */
  cancelLabel?: string;
}

/**
 * #1177 (spec §3.3) — a field changed both here and elsewhere (another tab, or
 * an extraction merge). The dealer picks per field; "keep yours" is the default.
 */
export function ChangedElsewhereDialog({
  open,
  conflicts,
  merged,
  onResolve,
  onCancel,
  cancelLabel = 'Cancel',
}: ChangedElsewhereDialogProps) {
  const [choices, setChoices] = useState<Record<string, ConflictChoice>>({});

  useEffect(() => {
    if (open) setChoices(Object.fromEntries(conflicts.map((conflict) => [conflict.path, 'mine' as const])));
  }, [open, conflicts]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Changed elsewhere</DialogTitle>
          <DialogDescription>
            These fields changed somewhere else while you were editing. Choose the value to keep for each.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 min-h-0 overflow-y-auto">
          {conflicts.map((conflict) => {
            const label = describeChangePath(conflict.path, merged);
            const idBase = `conflict-${conflict.path.replace(/[^a-zA-Z0-9]/g, '-')}`;
            return (
              <fieldset key={conflict.path} aria-label={label} className="space-y-2">
                <legend className="text-sm font-semibold">{label}</legend>
                <RadioGroup
                  value={choices[conflict.path] ?? 'mine'}
                  onValueChange={(value) => setChoices((prev) => ({ ...prev, [conflict.path]: value as ConflictChoice }))}
                  className="space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="mine" id={`${idBase}-mine`} />
                    <Label htmlFor={`${idBase}-mine`} className="text-sm font-normal">
                      {`Keep yours: ${formatChangeValue(conflict.mine)}`}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="theirs" id={`${idBase}-theirs`} />
                    <Label htmlFor={`${idBase}-theirs`} className="text-sm font-normal">
                      {`Use the new value: ${formatChangeValue(conflict.theirs)}`}
                    </Label>
                  </div>
                </RadioGroup>
              </fieldset>
            );
          })}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="button" size="sm" onClick={() => onResolve(choices)}>
            Save with these choices
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
